// src/pages/RegistroSALPage.js
// Registro Computo Metrico / SAL — una riga per ogni commessa che ne ha uno, con filtri.
// Cliccando una riga si apre direttamente il Computo Metrico, qui stesso — stesso schema
// già usato dagli altri registri di Nexiora (Sopralluoghi, Segnalazioni, ecc.).
import React, { useState, useEffect } from "react";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useOrg } from "../contexts/OrgContext";
import { Search, FileText, ChevronLeft } from "lucide-react";
import { calcolaSAL } from "../utils/calcoloSAL";
import ComputoSALTab from "../components/ComputoSALTab";

function fmt(n) {
  return (parseFloat(n)||0).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}
function fmtPerc(n) {
  return (parseFloat(n)||0).toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}
function dataItaliana(iso) {
  if (!iso) return "—";
  return iso.split("-").reverse().join("/");
}

const MESI_FILTRO = ["01-Gen","02-Feb","03-Mar","04-Apr","05-Mag","06-Giu","07-Lug","08-Ago","09-Set","10-Ott","11-Nov","12-Dic"];

export default function RegistroSALPage({ onNavigate }) {
  const { effectiveOrgId: orgId } = useOrg();
  const [computi, setComputi] = useState([]);
  const [cerca, setCerca] = useState("");
  const [clienteFiltro, setClienteFiltro] = useState("");
  const [mese, setMese] = useState("");
  const [anno, setAnno] = useState("");
  const [statoFiltro, setStatoFiltro] = useState("");
  const [selezionato, setSelezionato] = useState(null);

  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(query(collection(db,"computiSAL"), where("orgId","==",orgId)),
      snap => setComputi(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, [orgId]);

  const clientiDisponibili = [...new Set(computi.map(c=>c.clienteNome).filter(Boolean))].sort((a,b)=>a.localeCompare(b,"it"));
  const anniDisponibili = [...new Set(computi.flatMap(c=>(c.periodi||[]).map(p=>p.data?.split("-")[0])).filter(Boolean))].sort((a,b)=>b-a);

  const arricchiti = computi.map(c => {
    const periodi = c.periodi||[];
    const ultimo = periodi[periodi.length-1] || null;
    const dati = calcolaSAL(c.righe||[], periodi, c.fatturato||0);
    const statoCorrente = !ultimo ? "senza-sal" : ultimo.stato==="salvato" ? "tutto-salvato" : "bozza-aperta";
    return { ...c, ultimo, dati, statoCorrente };
  });

  const filtrati = arricchiti.filter(c => {
    if (clienteFiltro && c.clienteNome!==clienteFiltro) return false;
    if (mese && c.ultimo?.data?.split("-")[1]!==mese) return false;
    if (anno && c.ultimo?.data?.split("-")[0]!==anno) return false;
    if (statoFiltro && c.statoCorrente!==statoFiltro) return false;
    if (cerca) {
      const s = cerca.toLowerCase();
      return (c.projectName||"").toLowerCase().includes(s) || (c.clienteNome||"").toLowerCase().includes(s) || (c.codiceCommessa||"").toLowerCase().includes(s);
    }
    return true;
  });

  const inp = {padding:"6px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none"};
  const STATO_LABEL = { "senza-sal":{label:"Nessun SAL",bg:"#f3f4f6",col:"#374151"}, "bozza-aperta":{label:"SAL in bozza",bg:"#fef3c7",col:"#92400e"}, "tutto-salvato":{label:"Tutto salvato",bg:"#d1fae5",col:"#065f46"} };

  if (selezionato) {
    return (
      <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
        <div style={{background:"#fff",borderBottom:"1px solid #e5e7eb",padding:"10px 20px",flexShrink:0}}>
          <button onClick={()=>setSelezionato(null)} style={{background:"none",border:"none",cursor:"pointer",color:"#7c3aed",display:"flex",alignItems:"center",gap:4,fontSize:13,fontWeight:600}}>
            <ChevronLeft size={15}/> Torna al registro
          </button>
        </div>
        <div style={{flex:1,overflow:"hidden"}}>
          <ComputoSALTab
            projectId={selezionato.projectId} projectName={selezionato.projectName}
            codiceCommessa={selezionato.codiceCommessa} clienteNome={selezionato.clienteNome}
            orgId={orgId}/>
        </div>
      </div>
    );
  }

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
      <div style={{background:"#fff",borderBottom:"1px solid #e5e7eb",padding:"16px 24px",flexShrink:0}}>
        <h2 style={{fontSize:18,fontWeight:800,color:"#1e1b4b",margin:0,display:"flex",alignItems:"center",gap:8}}>
          <FileText size={19} color="#7c3aed"/> Registro Computo Metrico / SAL
        </h2>
        <p style={{fontSize:12,color:"#374151",margin:"2px 0 12px"}}>{filtrati.length} commessa/e con Computo Metrico</p>

        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
          <div style={{position:"relative"}}>
            <Search size={13} style={{position:"absolute",left:8,top:"50%",transform:"translateY(-50%)",color:"#9ca3af"}}/>
            <input value={cerca} onChange={e=>setCerca(e.target.value)} placeholder="Cerca commessa, cliente, codice..."
              style={{...inp,paddingLeft:26,width:230}}/>
          </div>
          <select value={clienteFiltro} onChange={e=>setClienteFiltro(e.target.value)} style={inp}>
            <option value="">Tutti i clienti</option>
            {clientiDisponibili.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
          <select value={mese} onChange={e=>setMese(e.target.value)} style={inp}>
            <option value="">Tutti i mesi</option>
            {MESI_FILTRO.map(m=><option key={m} value={m.slice(0,2)}>{m.slice(3)}</option>)}
          </select>
          <select value={anno} onChange={e=>setAnno(e.target.value)} style={inp}>
            <option value="">Tutti gli anni</option>
            {anniDisponibili.map(a=><option key={a} value={a}>{a}</option>)}
          </select>
          <select value={statoFiltro} onChange={e=>setStatoFiltro(e.target.value)} style={inp}>
            <option value="">Tutti gli stati</option>
            {Object.entries(STATO_LABEL).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
          </select>
          {(cerca||clienteFiltro||mese||anno||statoFiltro) && (
            <button onClick={()=>{setCerca("");setClienteFiltro("");setMese("");setAnno("");setStatoFiltro("");}}
              style={{padding:"6px 12px",border:"1px solid #e5e7eb",borderRadius:8,background:"#fff",fontSize:12,cursor:"pointer"}}>
              × Reset
            </button>
          )}
        </div>
      </div>

      <div style={{flex:1,overflow:"auto"}}>
        {filtrati.length===0 ? (
          <div style={{textAlign:"center",padding:60,color:"#374151"}}>
            <div style={{fontSize:48,marginBottom:12,opacity:.15}}>📐</div>
            <div style={{fontSize:14,fontWeight:600}}>Nessun Computo Metrico trovato</div>
            <div style={{fontSize:12,marginTop:4,color:"#6b7280"}}>Si crea aprendo la scheda "SAL" dentro una commessa</div>
          </div>
        ) : (
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
            <thead style={{position:"sticky",top:0,zIndex:5}}>
              <tr style={{background:"#1e1b4b"}}>
                {["Commessa","Cliente","Ultimo SAL","Avanzamento","Importo","Stato"].map(h=>(
                  <th key={h} style={{padding:"9px 12px",textAlign:"left",color:"#fff",fontSize:10,fontWeight:700}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtrati.map((c,i)=>{
                const st = STATO_LABEL[c.statoCorrente];
                return (
                  <tr key={c.id} style={{background:i%2===0?"#fff":"#fafafa",borderBottom:"1px solid #f3f4f6",cursor:"pointer"}}
                    onClick={()=>setSelezionato(c)}>
                    <td style={{padding:"9px 12px"}}>
                      <div style={{fontWeight:700,color:"#111827"}}>{c.codiceCommessa?`[${c.codiceCommessa}] `:""}{c.projectName}</div>
                      <div style={{fontSize:10.5,color:"#6b7280"}}>{c.titolo}</div>
                    </td>
                    <td style={{padding:"9px 12px",color:"#374151"}}>{c.clienteNome||"—"}</td>
                    <td style={{padding:"9px 12px",color:"#374151"}}>{c.ultimo?dataItaliana(c.ultimo.data):"—"}</td>
                    <td style={{padding:"9px 12px",fontWeight:700,color:"#7c3aed"}}>{fmtPerc(c.dati.avanzamentoComplessivo)}%</td>
                    <td style={{padding:"9px 12px",fontWeight:700,color:"#059669"}}>€{fmt(c.dati.totaleGenerale)}</td>
                    <td style={{padding:"9px 12px"}}><span style={{fontSize:10,padding:"2px 8px",borderRadius:10,background:st.bg,color:st.col,fontWeight:700}}>{st.label}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
